// @ts-expect-error - The type is exported
import type { SearchKey } from "@m10s/search-frontend-clients/quest";
import { useScreenSize } from "@mobility-item-page/client";
import type { PropsWithChildren } from "react";
import { Children, useCallback, useRef, useState } from "react";
import { trackCarouselScroll } from "src/utils/pulseHelper.ts";
import { Icon } from "./Icon.tsx";

interface Props extends PropsWithChildren {
  className?: string;
  searchKey?: SearchKey;
  /**
   * The offset from the top of the container to position the scroll buttons.
   * This can be used to vertically center the buttons relative to the cards content usually when you want to align the buttons with the image in the card instead of the whole card height.
   * The calculation is as follows:
   * ```
   * top-[calc(((100% - offset)/2) - buttonHeight)]
   * ```
   * By default, it centers the buttons without an offset.
   *
   * **Example:**
   * With an image on top, then 16px gap and 44px text heigh:
   * ```
   * scrollButtonOffset: 60
   * ```
   */
  scrollButtonOffset?: number;
}

export const ScrollContainer = (props: Props) => {
  const [showBackButton, setShowBackButton] = useState(false);
  const [showForwardButton, setShowForwardButton] = useState(true);
  const scrollIndex = useRef(0);
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  // Track whether we've already sent tracking events (per direction) so we only send once
  const hasTracked = useRef({ backward: false, forward: false });
  const { isMobile } = useScreenSize();

  const buttonsEnabled = Children.count(props.children) > 3 && !isMobile;

  const handleScroll = useCallback(
    (container: HTMLDivElement) => {
      const showBackButton = container.scrollLeft > 25;
      const showForwardButton = container.scrollWidth - container.offsetWidth - container.scrollLeft > 25;
      // On initial render the scrollWidth might be 0
      const showForwardOnInitialRender = container.scrollWidth === 0 && buttonsEnabled;

      setShowBackButton(showBackButton);
      setShowForwardButton(showForwardButton || showForwardOnInitialRender);
    },
    [buttonsEnabled],
  );

  const scrollContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", () => handleScroll(scrollContainer));
      }
      if (node) {
        setScrollContainer(node); // Store the node in state for later use
        node.addEventListener("scroll", () => handleScroll(node));
        handleScroll(node); // Initialize button states on mount
      }
    },
    [handleScroll, scrollContainer],
  );

  const scrollList = (direction: "forward" | "backward") => {
    if (scrollContainer) {
      const scrollAmount = scrollContainer.offsetWidth + 16;
      scrollIndex.current = direction === "forward" ? scrollIndex.current + 1 : scrollIndex.current - 1;
      const newScrollPosition = scrollIndex.current * scrollAmount;

      scrollContainer.scrollTo({ left: newScrollPosition, behavior: "smooth" });
    }
  };

  const topButtonDistanceStyle = props.scrollButtonOffset
    ? { top: `calc(((100% - ${props.scrollButtonOffset}px) / 2) - 27px)` }
    : { top: "calc((100% / 2) - 27px)" };

  return (
    <div
      className="relative"
      style={{
        ...(isMobile ? { marginRight: "-16px" } : { maxWidth: "948px" }),
      }}
    >
      {buttonsEnabled && (
        <button
          type="button"
          className={`absolute -left-24 s-surface-elevated-300 hover:s-surface-elevated-300-hover active:s-surface-elevated-300-active rounded-full w-48 h-48 flex items-center justify-center z-10 transition-all ${!showBackButton ? "opacity-0 pointer-events-none" : "opacity-100"}`}
          style={topButtonDistanceStyle}
          onClick={() => {
            if (!hasTracked.current.backward && props.searchKey) {
              trackCarouselScroll("backwards", props.searchKey);
              hasTracked.current.backward = true;
            }
            scrollList("backward");
          }}
        >
          <Icon name="ChevronLeft" size="medium" className="s-icon" />
        </button>
      )}
      <div className={props.className} ref={scrollContainerRef} style={{ scrollbarWidth: "none" }}>
        {props.children}
      </div>
      {buttonsEnabled && (
        <button
          type="button"
          className={`absolute -right-24 s-surface-elevated-300 hover:s-surface-elevated-300-hover active:s-surface-elevated-300-active rounded-full w-48 h-48 flex items-center justify-center z-10 transition-all ${!showForwardButton ? "opacity-0 pointer-events-none" : "opacity-100"}`}
          style={topButtonDistanceStyle}
          onClick={() => {
            if (!hasTracked.current.forward && props.searchKey) {
              trackCarouselScroll("forwards", props.searchKey);
              hasTracked.current.forward = true;
            }
            scrollList("forward");
          }}
        >
          <Icon name="ChevronRight" size="medium" className="s-icon" />
        </button>
      )}
    </div>
  );
};
